interface Envelope<T> {
  key: string;
  schemaVersion: number;
  cachedAt: number;
  data: T[];
}

export interface LoadedSnapshot<T> {
  data: T[];
  cachedAt: number;
}

// IndexedDB snapshot store keyed by the caller's key (which already embeds the
// tenant). clearAll wipes every key — all tenants — on sign-out / tenant switch.
export abstract class ScSnapshotStore<T> {
  protected abstract readonly dbName: string;
  protected abstract readonly storeName: string;
  // Bump when T's shape changes — a stale-shape blob is ignored on read.
  protected abstract readonly schemaVersion: number;

  private dbPromise: Promise<IDBDatabase | null> | null = null;

  async load(key: string): Promise<LoadedSnapshot<T> | null> {
    const db = await this.db();
    if (!db) return null;
    const stored = await req<Envelope<T> | undefined>(
      db.transaction(this.storeName, 'readonly').objectStore(this.storeName).get(key),
    ).catch(() => undefined);
    if (!stored || stored.schemaVersion !== this.schemaVersion) return null;
    return { data: stored.data, cachedAt: stored.cachedAt };
  }

  async save(key: string, data: T[]): Promise<void> {
    const db = await this.db();
    if (!db) return;
    const value: Envelope<T> = { key, schemaVersion: this.schemaVersion, cachedAt: Date.now(), data };
    // put() can throw synchronously (DataError) as well as reject — cache writes are best-effort.
    try {
      await req(db.transaction(this.storeName, 'readwrite').objectStore(this.storeName).put(value));
    } catch {
      /* ignore */
    }
  }

  async clearAll(): Promise<void> {
    const db = await this.db();
    if (!db) return;
    await req(
      db.transaction(this.storeName, 'readwrite').objectStore(this.storeName).clear(),
    ).catch(() => undefined);
  }

  private db(): Promise<IDBDatabase | null> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise<IDBDatabase | null>(resolve => {
      if (typeof indexedDB === 'undefined') return resolve(null);
      // v2 recreates the store: an earlier build keyed it on 'scopeKey'/'filterKey',
      // so a put() of a 'key'-keyed envelope would DataError against the stale schema.
      const open = indexedDB.open(this.dbName, 2);
      open.onupgradeneeded = () => {
        const db = open.result;
        if (db.objectStoreNames.contains(this.storeName)) db.deleteObjectStore(this.storeName);
        db.createObjectStore(this.storeName, { keyPath: 'key' });
      };
      open.onsuccess = () => resolve(open.result);
      open.onerror = () => resolve(null);
    });
    return this.dbPromise;
  }
}

function req<R>(request: IDBRequest<R>): Promise<R> {
  return new Promise<R>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
